import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { LanguageSelector } from '../components/LanguageSelector';
import DemoModal from '../components/DemoModal';
import { 
  Trophy, 
  Users, 
  BarChart3, 
  Globe, 
  Zap, 
  Shield,
  ChevronRight,
  Check,
  Star,
  Tag
} from 'lucide-react';

const featureIcons = [Trophy, Users, BarChart3, Globe, Zap, Shield];
const featureKeys = ['tournament', 'teams', 'stats', 'share', 'updates', 'security'];

const plans = [
  {
    name: 'Free',
    priceMonthly: 0,
    priceAnnual: 0,
    promoPrice: null,
    description: 'Perfect for trying out',
    features: ['1 tournament', '4 teams max', 'Basic features', 'Community support'],
    cta: 'Start Free',
    popular: false
  },
  {
    name: 'Starter',
    priceMonthly: 15,
    priceAnnual: 162,
    promoPrice: 9.99,
    description: 'For small leagues',
    features: ['3 tournaments', '8 teams per tournament', 'No ads', 'Email support', 'Custom colors'],
    cta: 'Get Started',
    popular: false
  },
  {
    name: 'Pro',
    priceMonthly: 39,
    priceAnnual: 421,
    promoPrice: 29.99,
    description: 'For serious organizers',
    features: ['Unlimited tournaments', '16 teams per tournament', 'Custom branding', 'Priority support', 'Sponsor management (5)'],
    cta: 'Go Pro',
    popular: true
  },
  {
    name: 'Enterprise',
    priceMonthly: 99,
    priceAnnual: 1069,
    promoPrice: 79.99,
    description: 'For organizations',
    features: ['Everything in Pro', 'White-label solution', 'API access', 'Dedicated support', 'Unlimited sponsors'],
    cta: 'Contact Sales',
    popular: false
  }
];

const testimonials = [
  {
    quote: 'ScoreCopilot transformed how we manage our indoor soccer league. The automated standings and shareable links are game-changers!',
    author: "Mike Chen",
    role: "League Director, Toronto Indoor Soccer"
  },
  {
    quote: "We used to spend hours on spreadsheets. Now everything is automated and our players love checking stats on their phones.",
    author: "Sarah Williams",
    role: "Tournament Organizer, Corporate Cup"
  },
  {
    quote: "The public tournament pages let our fans follow along without any hassle. Perfect for community tournaments.",
    author: "James Rodriguez",
    role: "Community Center Manager"
  }
];

export default function LandingPage() {
  const { t } = useTranslation();
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [demoOpen, setDemoOpen] = useState(false);
  
  const getPrice = (plan) => {
    return billingCycle === 'annual' ? plan.priceAnnual : plan.priceMonthly;
  };
  
  const getMonthlyEquivalent = (plan) => {
    if (billingCycle === 'annual' && plan.priceAnnual > 0) {
      return (plan.priceAnnual / 12).toFixed(0);
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-[#0f0f10]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src="/logo.jpg" 
                alt="Score Copilot" 
                className="h-12 w-auto object-contain"
              />
              <span className="font-heading text-2xl font-bold text-white uppercase tracking-wide">
                Score Copilot
              </span>
            </Link>
            <div className="hidden md:flex items-center gap-8">
              <Link to="/pricing" className="text-gray-400 hover:text-white transition-colors">
                {t('nav.pricing')}
              </Link>
              <Link to="/contact" className="text-gray-400 hover:text-white transition-colors">
                {t('nav.contact')}
              </Link>
              <Link to="/login" className="text-gray-400 hover:text-white transition-colors">
                {t('nav.signIn')}
              </Link>
              <LanguageSelector variant="minimal" />
              <Link to="/register">
                <Button 
                  data-testid="get-started-btn"
                  className="bg-emerald-500 hover:bg-emerald-600 text-white btn-lift"
                >
                  {t('nav.getStarted')}
                </Button>
              </Link>
            </div>
            <Link to="/register" className="md:hidden">
              <Button className="bg-emerald-500 hover:bg-emerald-600 text-white" size="sm">
                {t('nav.startFree')}
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=1920)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          <div className="absolute inset-0 hero-overlay" />
        </div>
        
        {/* Emerald Glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full opacity-20"
          style={{ background: 'radial-gradient(circle, rgba(16, 185, 129, 0.3) 0%, transparent 70%)' }}
        />

        <div className="relative z-10 max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 mb-8">
            <Zap className="text-emerald-500" size={16} />
            <span className="text-emerald-500 text-sm font-medium">{t('landing.hero.badge')}</span>
          </div>
          
          <h1 className="font-heading text-5xl md:text-7xl lg:text-8xl font-bold text-white uppercase tracking-tight mb-6">
            {t('landing.hero.title')}
          </h1>
          
          <p className="text-gray-400 text-lg max-w-2xl mx-auto mb-10">
            {t('landing.hero.subtitle')}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button 
                size="lg" 
                data-testid="hero-cta-btn"
                className="bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-6 text-lg btn-lift w-full sm:w-auto"
              >
                {t('landing.hero.cta')}
                <ChevronRight className="ml-2" size={20} />
              </Button>
            </Link>
            <Button 
              size="lg" 
              variant="outline"
              data-testid="demo-btn"
              onClick={() => setDemoOpen(true)}
              className="border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 px-8 py-6 text-lg w-full sm:w-auto"
            >
              <Globe className="mr-2" size={20} />
              {t('landing.hero.seeDemo')}
            </Button>
            <Link to="/pricing">
              <Button 
                size="lg" 
                variant="outline"
                className="border-white/20 text-white hover:bg-white/5 px-8 py-6 text-lg w-full sm:w-auto"
              >
                {t('landing.hero.viewPricing')}
              </Button>
            </Link>
          </div>
          
          <p className="text-gray-500 text-sm mt-6">
            Questions? <Link to="/contact" className="text-emerald-500 hover:text-emerald-400">{t('nav.contact')}</Link>
          </p>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 px-6 relative">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-white uppercase mb-4">
              {t('landing.features.title')}
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              {t('landing.features.subtitle')}
              All the tools to run professional tournaments, from registration to final standings.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featureKeys.map((key, index) => {
              const Icon = featureIcons[index];
              return (
                <div 
                  key={key}
                  className="bg-[#18181b] border border-white/10 rounded-xl p-6 card-hover"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="w-12 h-12 bg-emerald-500/10 rounded-lg flex items-center justify-center mb-4">
                    <Icon className="text-emerald-500" size={24} />
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-white uppercase mb-2">
                    {t(`landing.features.${key}.title`)}
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed">
                    {t(`landing.features.${key}.description`)}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 px-6 bg-[#0a0a0b]" id="pricing">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-white uppercase mb-4">
              {t('pricing.title')}
            </h2>
            <p className="text-gray-400 text-lg">
              {t('pricing.subtitle')}
            </p>
          </div>

          {/* Billing Toggle */}
          <div className="flex flex-col items-center gap-4 mb-10">
            <div className="flex items-center bg-[#18181b] border border-white/10 rounded-lg p-1">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`px-6 py-2 rounded-md text-sm font-medium transition-all ${
                  billingCycle === 'monthly'
                    ? 'bg-emerald-500 text-white'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingCycle('annual')}
                className={`px-6 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${
                  billingCycle === 'annual'
                    ? 'bg-emerald-500 text-white'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                Annual
                <span className="px-2 py-0.5 bg-yellow-500 text-black text-xs rounded-full font-bold">
                  Save 10%
                </span>
              </button>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Tag className="text-emerald-500" size={16} />
              <span className="text-gray-400">Use code</span>
              <span className="text-emerald-500 font-mono font-bold">SCORE5</span>
              <span className="text-gray-400">for 5% off (limited time)</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {plans.map((plan, index) => {
              const price = getPrice(plan);
              const monthlyEquiv = getMonthlyEquivalent(plan);
              const hasPromo = plan.promoPrice && billingCycle === 'monthly';
              
              return (
                <div 
                  key={index}
                  className={`relative bg-[#18181b] border rounded-xl p-6 ${
                    plan.popular 
                      ? 'border-emerald-500 emerald-glow' 
                      : 'border-white/10'
                  }`}
                >
                  {plan.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-emerald-500 rounded-full text-xs font-medium text-white">
                      Most Popular
                    </div>
                  )}
                  {hasPromo && (
                    <div className="absolute -top-3 right-4 px-3 py-1 bg-orange-500 rounded-full text-xs font-bold text-white animate-pulse">
                      Limited Time
                    </div>
                  )}
                  <div className="mb-6">
                    <h3 className="font-heading text-2xl font-bold text-white uppercase">{plan.name}</h3>
                    <p className="text-gray-500 text-sm mt-1">{plan.description}</p>
                  </div>
                  <div className="mb-6">
                    {hasPromo ? (
                      <>
                        <div className="flex items-baseline gap-2">
                          <span className="text-lg text-gray-500 line-through">${plan.priceMonthly}</span>
                          <span className="text-4xl font-bold text-emerald-400">${plan.promoPrice}</span>
                          <span className="text-gray-500">/month</span>
                        </div>
                        <p className="text-orange-400 text-xs mt-1 font-medium">
                          Lock in this price forever when you sign up now
                        </p>
                      </>
                    ) : (
                      <>
                        <span className="text-4xl font-bold text-white">${price}</span>
                        <span className="text-gray-500">/{billingCycle === 'annual' ? 'year' : 'month'}</span>
                        {monthlyEquiv && (
                          <p className="text-emerald-500 text-sm mt-1">
                            ${monthlyEquiv}/mo equivalent
                          </p>
                        )}
                      </>
                    )}
                  </div>
                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-3 text-sm text-gray-300">
                        <Check className="text-emerald-500 flex-shrink-0" size={16} />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Link to="/register">
                    <Button 
                      className={`w-full ${
                        plan.popular 
                          ? 'bg-emerald-500 hover:bg-emerald-600 text-white' 
                          : 'bg-white/5 hover:bg-white/10 text-white border border-white/10'
                      }`}
                      data-testid={`plan-${plan.name.toLowerCase()}-btn`}
                    >
                      {plan.cta}
                    </Button>
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-white uppercase mb-4">
              Trusted by Organizers
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <div 
                key={index}
                className="bg-[#18181b] border border-white/10 rounded-xl p-6"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="text-emerald-500 fill-emerald-500" size={16} />
                  ))}
                </div>
                <p className="text-gray-300 text-sm leading-relaxed mb-6">
                  {testimonial.quote}
                </p>
                <div>
                  <p className="text-white font-medium">{testimonial.author}</p>
                  <p className="text-gray-500 text-sm">{testimonial.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-b from-[#0f0f10] to-[#0a0a0b]">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-white uppercase mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-gray-400 text-lg mb-8">
            Create your first tournament in minutes. No credit card required.
          </p>
          <Link to="/register">
            <Button 
              size="lg"
              data-testid="footer-cta-btn"
              className="bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-6 text-lg btn-lift"
            >
              Start Free Tournament
              <ChevronRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/10">
        <div className="max-w-7xl mx-auto">
          {/* Sponsor Logos */}
          <div className="flex items-center justify-center gap-4 mb-8">
            <img src="/cnvt-logo.jpg" alt="CNVT" className="w-10 h-10 rounded object-contain" />
            <span className="text-gray-600 text-sm">|</span>
            <img src="/kilimanjaro-logo.png" alt="Kilimanjaro" className="h-10 object-contain" />
            <span className="text-gray-600 text-sm">|</span>
            <img src="/africremit-logo.jpg" alt="Afric Remit" className="h-10 object-contain rounded" />
          </div>
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img 
                src="/logo.jpg" 
                alt="Score Copilot" 
                className="h-10 w-auto object-contain"
              />
              <span className="font-heading text-xl font-bold text-white uppercase">Score Copilot</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-gray-500">
              <Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link>
              <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
              <a href="#" className="hover:text-white transition-colors">Privacy</a>
              <a href="#" className="hover:text-white transition-colors">Terms</a>
            </div>
            <p className="text-gray-500 text-sm">
              © 2026 Sports Copilot. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      <DemoModal isOpen={demoOpen} onClose={() => setDemoOpen(false)} />
    </div>
  );
}
